/**
 * Saved-view rules for summary (ledger) reports, e.g. the Sales Summary.
 *
 * A summary's view is just the date range and filters (`LedgerViewConfig`):
 * the API rejects the table-only fields (groups, columns, KPIs, …), so the
 * portal never writes them. Range / filter normalizing is shared with the
 * table reports (model/viewConfig).
 */
import { deepEqual } from "../../../../../utils/deepEqual";
import {
  LedgerViewConfig,
  ReportV2Definition,
  RunLedgerReportRequest,
  VIEW_CONFIG_VERSION,
} from "../../../../../types/reportsV2";
import { ViewRules } from "../../hooks/useViewDraft";
import {
  cleanFilters,
  normalizeRange,
  normalizeShownFilters,
  requestKeyOf,
} from "../../model/viewConfig";

type Raw = Record<string, unknown>;

/** A complete, validator-safe summary view from a stored (maybe empty) one. */
export function normalizeLedgerView(
  def: ReportV2Definition,
  stored: unknown,
): LedgerViewConfig {
  const raw = (stored && typeof stored === "object" ? stored : {}) as Raw;
  const d = def.default_view;
  return {
    v: VIEW_CONFIG_VERSION,
    range: "range" in raw ? normalizeRange(def, raw.range) : { ...d.range },
    filters: cleanFilters(def, "filters" in raw ? raw.filters : d.filters),
    shown_filters: normalizeShownFilters(def, raw.shown_filters),
  };
}

/** The run body of a summary: only the range and filters apply. */
export const ledgerRunRequest = (
  view: LedgerViewConfig,
): RunLedgerReportRequest => ({ range: view.range, filters: view.filters });

/** Same view? Ignores the order filter values were picked in. */
export const ledgerViewsEqual = (
  a: LedgerViewConfig,
  b: LedgerViewConfig,
): boolean =>
  requestKeyOf(ledgerRunRequest(a)) === requestKeyOf(ledgerRunRequest(b)) &&
  deepEqual([...a.shown_filters].sort(), [...b.shown_filters].sort());

/** useViewDraft rules for a summary report. */
export const ledgerViewRules = (
  def: ReportV2Definition,
): ViewRules<LedgerViewConfig> => ({
  normalize: (stored) => normalizeLedgerView(def, stored),
  equal: ledgerViewsEqual,
});
